import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface CmsImageProps {
    src: string
    alt: string
    sizes: string
    preload?: boolean
    className?: string
}

// Images from the site's own storage are resized and compressed automatically.
// Anything else (an outside link pasted into the CMS) falls back to a plain image,
// so an unexpected address can never break the page.
const OPTIMIZED_HOST = /(^|\.)cdn\.modulify\.website$/i

const canOptimize = (src: string): boolean => {
    if (src.startsWith('/') && !src.startsWith('//')) return true

    try {
        const { protocol, hostname } = new URL(src)

        return protocol === 'https:' && OPTIMIZED_HOST.test(hostname)
    }
    catch {
        return false
    }
}

// Fills its parent, which must be `relative` and have a size or aspect ratio.
export const CmsImage: FC<CmsImageProps> = ({
    src,
    alt,
    sizes,
    preload = false,
    className
}): ReactNode => {
    const classes = cn('object-cover', className)

    if (canOptimize(src)) {
        return (
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                preload={preload}
                className={classes}
            />
        )
    }

    return (
        <img
            src={src}
            alt={alt}
            loading={preload ? 'eager' : 'lazy'}
            decoding='async'
            className={cn('absolute inset-0 size-full', classes)}
        />
    )
}
